import { X, Loader2, RefreshCw } from 'lucide-react';
import { useState, useEffect } from 'react';
import { customersService, SubscriptionFrequency } from '../../services/customers.service';
import { customerTypesService, CustomerType } from '../../services/customerTypes.service';
import { subscriptionPlansService, SubscriptionPlan } from '../../services/subscriptionPlans.service';
import { useCurrency } from '../../hooks/useCurrency';
import { toast } from 'sonner';

const FREQ_LABELS: Record<string, string> = { MONTHLY: 'Monthly', QUARTERLY: 'Quarterly', ANNUALLY: 'Annually' };

function calcSubDates(startDate: string, frequency: string): { endDate: string; nextDate: string } {
  if (!startDate || !frequency) return { endDate: '', nextDate: '' };
  const d = new Date(startDate);
  if (frequency === 'MONTHLY') d.setMonth(d.getMonth() + 1);
  else if (frequency === 'QUARTERLY') d.setMonth(d.getMonth() + 3);
  else if (frequency === 'ANNUALLY') d.setFullYear(d.getFullYear() + 1);
  d.setDate(d.getDate() - 1);
  const endDate = d.toISOString().slice(0, 10);
  d.setDate(d.getDate() + 1);
  const nextDate = d.toISOString().slice(0, 10);
  return { endDate, nextDate };
}

interface EditCustomerModalProps {
  customerId: string;
  onClose: () => void;
  onSave: (customerId: string, data: any) => void;
}

const INPUT = 'w-full px-4 py-2.5 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500';
const LABEL = 'block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5';

export function EditCustomerModal({ customerId, onClose, onSave }: EditCustomerModalProps) {
  const [types, setTypes] = useState<CustomerType[]>([]);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { formatCurrency } = useCurrency();

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    membershipTier: 'BRONZE' as 'BRONZE' | 'SILVER' | 'GOLD' | 'PLATINUM',
    isActive: true,
    customerTypeId: '',
    subscriptionFrequency: '' as (SubscriptionFrequency | ''),
    subscriptionPlanId: '',
    subscriptionStartDate: '',
    subscriptionEndDate: '',
    nextPaymentDate: '',
  });

  const selectedType = types.find((t) => t.id === form.customerTypeId) ?? null;
  const isSubscriptionBased = selectedType?.isSubscriptionBased ?? false;
  const selectedPlan = plans.find((p) => p.id === form.subscriptionPlanId) ?? null;

  useEffect(() => {
    customerTypesService.getAll().then((r) => { if (r.success && r.data) setTypes(r.data); });
  }, []);

  const set = (field: string, value: any) => setForm((f) => ({ ...f, [field]: value }));

  useEffect(() => {
    customersService.getCustomerById(customerId)
      .then((res) => {
        if (res.success && res.data) {
          const c = res.data;
          setForm({
            firstName: c.firstName || '',
            lastName: c.lastName || '',
            email: c.email || '',
            phone: c.phone || '',
            address: c.address || '',
            city: c.city || '',
            country: c.country || '',
            membershipTier: c.membershipTier || 'BRONZE',
            isActive: c.isActive,
            customerTypeId: c.customerTypeId || '',
            subscriptionFrequency: (c.subscriptionFrequency || '') as (SubscriptionFrequency | ''),
            subscriptionPlanId: c.subscriptionPlanId || '',
            subscriptionStartDate: c.subscriptionStartDate ? c.subscriptionStartDate.slice(0, 10) : '',
            subscriptionEndDate: c.subscriptionEndDate ? c.subscriptionEndDate.slice(0, 10) : '',
            nextPaymentDate: c.nextPaymentDate ? c.nextPaymentDate.slice(0, 10) : '',
          });
        }
      })
      .catch(() => toast.error('Failed to load customer'))
      .finally(() => setLoading(false));
  }, [customerId]);

  // Load plans when customerTypeId changes
  useEffect(() => {
    const st = types.find((t) => t.id === form.customerTypeId);
    if (st?.isSubscriptionBased && form.customerTypeId) {
      subscriptionPlansService.getByTypeId(form.customerTypeId)
        .then((r) => { if (r.success && r.data) setPlans(r.data.filter((p) => p.isActive)); })
        .catch(() => setPlans([]));
    } else {
      setPlans([]);
    }
  }, [form.customerTypeId, types]);

  // Auto-fill frequency when plan is selected (also fires once plans finish loading)
  useEffect(() => {
    const plan = plans.find((p) => p.id === form.subscriptionPlanId);
    if (!plan?.defaultFrequency) return;
    setForm((f) => {
      // Only overwrite frequency if not already set
      const freq = f.subscriptionFrequency || plan.defaultFrequency!;
      // Only auto-calc dates if they're blank (don't overwrite saved values)
      if (f.subscriptionEndDate && f.nextPaymentDate) return { ...f, subscriptionFrequency: freq };
      const { endDate, nextDate } = calcSubDates(f.subscriptionStartDate, freq);
      return { ...f, subscriptionFrequency: freq, subscriptionEndDate: endDate, nextPaymentDate: nextDate };
    });
  }, [form.subscriptionPlanId, plans]); // re-run once plans are loaded

  // Auto-calculate end date and next payment when start date or frequency changes
  useEffect(() => {
    if (!form.subscriptionStartDate || !form.subscriptionFrequency) return;
    const { endDate, nextDate } = calcSubDates(form.subscriptionStartDate, form.subscriptionFrequency);
    setForm((f) => ({ ...f, subscriptionEndDate: endDate, nextPaymentDate: nextDate }));
  }, [form.subscriptionStartDate, form.subscriptionFrequency]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const dto: any = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone || undefined,
        address: form.address || undefined,
        city: form.city || undefined,
        country: form.country || undefined,
        membershipTier: form.membershipTier,
        isActive: form.isActive,
        customerTypeId: form.customerTypeId || undefined,
      };
      const selectedType = types.find((t) => t.id === form.customerTypeId) ?? null;
      const isSubscriptionBased = selectedType?.isSubscriptionBased ?? false;
      if (isSubscriptionBased) {
        dto.subscriptionFrequency = form.subscriptionFrequency || undefined;
        dto.subscriptionPlanId = form.subscriptionPlanId || undefined;
        dto.subscriptionStartDate = form.subscriptionStartDate || undefined;
        dto.subscriptionEndDate = form.subscriptionEndDate || undefined;
        dto.nextPaymentDate = form.nextPaymentDate || undefined;
      }
      const res = await customersService.updateCustomer(customerId, dto);
      if (res.success) {
        toast.success('Customer updated successfully');
        onSave(customerId, res.data);
        onClose();
      } else {
        toast.error('Failed to update customer');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update customer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-800">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Edit Customer</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Update customer information</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={LABEL}>First Name *</label>
                    <input type="text" value={form.firstName} onChange={(e) => set('firstName', e.target.value)} required className={INPUT} />
                  </div>
                  <div>
                    <label className={LABEL}>Last Name *</label>
                    <input type="text" value={form.lastName} onChange={(e) => set('lastName', e.target.value)} required className={INPUT} />
                  </div>
                  <div>
                    <label className={LABEL}>Email *</label>
                    <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} required className={INPUT} />
                  </div>
                  <div>
                    <label className={LABEL}>Phone</label>
                    <input type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} className={INPUT} />
                  </div>
                  <div>
                    <label className={LABEL}>Address</label>
                    <input type="text" value={form.address} onChange={(e) => set('address', e.target.value)} className={INPUT} />
                  </div>
                  <div>
                    <label className={LABEL}>City</label>
                    <input type="text" value={form.city} onChange={(e) => set('city', e.target.value)} className={INPUT} />
                  </div>
                  <div>
                    <label className={LABEL}>Membership Tier</label>
                    <select value={form.membershipTier} onChange={(e) => set('membershipTier', e.target.value)} className={INPUT}>
                      <option value="BRONZE">Bronze</option>
                      <option value="SILVER">Silver</option>
                      <option value="GOLD">Gold</option>
                      <option value="PLATINUM">Platinum</option>
                    </select>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => set('isActive', e.target.checked)}
                      className="w-4 h-4 text-indigo-600 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500"
                    />
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Active customer</span>
                  </label>
                </div>
              </div>

              {/* Customer Type */}
              <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Customer Type</h3>
                <select
                  value={form.customerTypeId}
                  onChange={(e) => set('customerTypeId', e.target.value)}
                  className={INPUT}
                >
                  <option value="">— No type —</option>
                  {types.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                {isSubscriptionBased && (
                  <p className="text-xs text-indigo-500 dark:text-indigo-400 mt-1.5 flex items-center gap-1">
                    <RefreshCw className="w-3 h-3" /> Subscription-based type
                  </p>
                )}
              </div>

              {/* Subscription Fields */}
              {isSubscriptionBased && (
                <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Subscription Details</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className={LABEL}>Subscription Plan</label>
                      <select value={form.subscriptionPlanId} onChange={(e) => set('subscriptionPlanId', e.target.value)} className={INPUT}>
                        <option value="">— Select a plan —</option>
                        {plans.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}{p.price ? ` — ${formatCurrency(p.price)}` : ''}{p.defaultFrequency ? ` / ${FREQ_LABELS[p.defaultFrequency]}` : ''}
                          </option>
                        ))}
                      </select>
                      {plans.length === 0 && (
                        <p className="text-xs text-amber-500 dark:text-amber-400 mt-1">No active plans for this type. Add plans via Customer Types.</p>
                      )}
                      {selectedPlan && (
                        <div className="mt-2 flex flex-wrap items-center gap-3 px-3 py-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                          {selectedPlan.price && (
                            <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">{formatCurrency(selectedPlan.price)}</span>
                          )}
                          {selectedPlan.defaultFrequency && (
                            <span className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">{FREQ_LABELS[selectedPlan.defaultFrequency]}</span>
                          )}
                          {selectedPlan.description && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">{selectedPlan.description}</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className={LABEL}>Billing Frequency</label>
                      <select value={form.subscriptionFrequency} onChange={(e) => set('subscriptionFrequency', e.target.value)} className={INPUT}>
                        <option value="">— None —</option>
                        <option value="MONTHLY">Monthly</option>
                        <option value="QUARTERLY">Quarterly</option>
                        <option value="ANNUALLY">Annually</option>
                      </select>
                    </div>
                    <div>
                      <label className={LABEL}>Start Date</label>
                      <input type="date" value={form.subscriptionStartDate} onChange={(e) => set('subscriptionStartDate', e.target.value)} className={INPUT} />
                    </div>
                    <div>
                      <label className={LABEL}>
                        End Date
                        {form.subscriptionEndDate && <span className="ml-1 text-indigo-500 dark:text-indigo-400 font-normal text-[10px] uppercase tracking-wide">auto</span>}
                      </label>
                      <input type="date" value={form.subscriptionEndDate} onChange={(e) => set('subscriptionEndDate', e.target.value)} className={INPUT} />
                    </div>
                    <div>
                      <label className={LABEL}>
                        Next Payment Date
                        {form.nextPaymentDate && <span className="ml-1 text-indigo-500 dark:text-indigo-400 font-normal text-[10px] uppercase tracking-wide">auto</span>}
                      </label>
                      <input type="date" value={form.nextPaymentDate} onChange={(e) => set('nextPaymentDate', e.target.value)} className={INPUT} />
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 pt-2">
                <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-60">
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Save Changes
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
